import type { ApiError } from '@shared/contracts/common'

export class ApiRequestError extends Error {
  readonly status: number
  readonly code: string
  readonly details: unknown
  /** The server's `x-request-id`, quoted in problem reports so staff can find the request. */
  readonly requestId: string | null
  constructor(status: number, body: ApiError['error'], requestId: string | null = null) {
    super(body.message)
    this.name = 'ApiRequestError'
    this.status = status
    this.code = body.code
    this.details = body.details
    this.requestId = requestId
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  query?: Record<string, string | number | boolean | undefined | null>
}

function withQuery(path: string, query?: RequestOptions['query']) {
  if (!query) return path
  const url = new URL(path, window.location.origin)
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue
    url.searchParams.set(k, String(v))
  }
  return url.pathname + url.search
}

export async function api<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { body, query, headers, ...rest } = opts
  const isForm = body instanceof FormData
  const res = await fetch(withQuery(path, query), {
    ...rest,
    credentials: 'same-origin',
    headers: {
      accept: 'application/json',
      ...(body !== undefined && !isForm ? { 'content-type': 'application/json' } : {}),
      ...(headers as Record<string, string>),
    },
    body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
  })
  if (res.status === 204) return undefined as T
  const text = await res.text()
  let json: unknown = null
  if (text) {
    try {
      json = JSON.parse(text)
    } catch {
      json = null
    }
  }
  if (!res.ok) {
    const err = (json as ApiError | null)?.error ?? {
      code: 'http_error',
      message: res.statusText || 'Request failed',
      details: null,
    }
    throw new ApiRequestError(res.status, err, res.headers.get('x-request-id'))
  }
  return json as T
}

export const http = {
  get: <T>(path: string, query?: RequestOptions['query']) => api<T>(path, { method: 'GET', query }),
  post: <T>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => api<T>(path, { method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown) => api<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => api<T>(path, { method: 'DELETE' }),
}

export async function uploadDocument(
  file: File,
  meta: {
    kind?: string
    visibility?: 'private' | 'internal' | 'public'
    entityType?: string
    entityId?: string
  } = {},
) {
  const form = new FormData()
  form.set('file', file)
  for (const [k, v] of Object.entries(meta)) if (v) form.set(k, v)
  return api<{
    id: string
    filename: string
    contentType: string
    sizeBytes: number
    kind: string
    visibility: string
    url: string
  }>('/api/v1/documents', { method: 'POST', body: form })
}
